import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Snackbar, Alert, CircularProgress } from '@mui/material';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import TreatmentInpatientChartView from './TreatmentInpatientChartView';
import { fetchRecordsPatientAllSurveysService } from '../../../services';
import { treatmentAdministrationService, TimeSlotName } from '../../../services/treatmentAdministration';
import { PERMISSION } from '../../../components/investigation/share/user_roles';
import { formatDateToISO, getWeekStart, getWeekEnd } from '../../../utils/dateUtils';
import { useSnackBarState } from '../../../hooks';
import { getCurrentResearcherUuid } from '../../../utils';
import { getDateFromStringOrDate } from '../../../utils/bill';

type TimeSlot = {
    status: "successful" | "not successful" | null;
    administrationId?: number,
    administeredBy?: string;
    createdAt?: string;
    reasonNotAdministered?: string;
};

interface TreatmentInpatientChartProps extends LocalizeContextProps {
    initialTreatments?: Array<any>;
    patientUuid?: string;
    uuidInvestigation?: string;
    language?: string;
}

const TreatmentInpatientChart: React.FC<TreatmentInpatientChartProps> = (props) => {
    const [chartData, setChartData] = useState<Array<{
        drug: string;
        quantity: number;
        doseAmount: string;
        doseUnit: string;
        frequency: string;
        route: string;
        stopped: string;
        treatmentPostId?: number;
        startDate?: Date;
        endDate?: Date;
        stoppedDate?: Date;
        administrations: { [dateKey: string]: { timeSlots: { [slotName: string]: TimeSlot } } };
    }>>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();

    //Tracks the chart data. This avoids adding this variable to the dependency array in loadAdministrationsForWeek so in this way prevents unnecessary re-renders
    const chartDataRef = useRef(chartData);         

    // Add ref to track the current week being loaded and prevent race conditions. Also it prevents duplicate requests
    const loadingWeekRef = useRef<string | null>(null);

    //In case the user navigates too quickly, this can abort the first request and only show the result of the second request
    const abortControllerRef = useRef<AbortController | null>(null);
    
    // Keep ref in sync with state
    useEffect(() => {
        chartDataRef.current = chartData;
    }, [chartData]);

    // Get investigation from Redux store to check permissions
    const investigation = useSelector((state: any) => {
        if (props.uuidInvestigation && state.investigations?.data) {
            return state.investigations.data.find((inv: any) => inv.uuid === props.uuidInvestigation);
        }
        return null;
    });

    // Check if user has permission to write administrations
    const hasWritePermission = investigation?.permissions?.includes(PERMISSION.TREATMENT_ADMINISTRATION_WRITE) ?? true; // Default to true if investigation not found

    /**
     * Fetches treatments from patient submissions to get treatmentPostId (postId from value object)
     */
    const fetchTreatmentPostIds = async (uuidInvestigation: string, uuidPatient: string): Promise<Record<string, number>> => {
        if (!uuidInvestigation || !uuidPatient) {
            return {};
        }

        try {
            const response = await fetchRecordsPatientAllSurveysService(uuidInvestigation, uuidPatient);
            
            // Extract treatmentPostIds from submissions - use postId/treatmentPostId from value object
            const treatmentPostIdMap: Record<string, number> = {};
            
            if (response && response.surveys) {
                response.surveys.forEach((survey: any) => {
                    if (survey.submissions) {
                        survey.submissions.forEach((submission: any) => {
                            if (submission.surveyRecords) {
                                submission.surveyRecords.forEach((record: any) => {
                                    if (record.surveyField && record.surveyField.type === "treatment_inpatient" && 
                                        record.value && Array.isArray(record.value)) {
                                        record.value.forEach((treatment: any) => {
                                            const drugName = treatment["treatment_inpatient"] || treatment.drug;
                                            if (drugName) {
                                                // Use postId or treatmentPostId from the treatment value object (per plan)
                                                // Fallback to record.id if postId is not in value object (temporary workaround)
                                                const postId = treatment.postId || treatment.treatmentPostId || record.id;
                                                if (postId) {
                                                    // Use drug name + route + frequency as key to match treatments
                                                    const route = treatment["treatment-route"] || "";
                                                    const frequency = treatment["treatment-frecuency"] || "";
                                                    const key = `${drugName}_${route}_${frequency}`;
                                                    treatmentPostIdMap[key] = postId;
                                                    // Also store by drug name alone for fallback
                                                    if (!treatmentPostIdMap[drugName]) {
                                                        treatmentPostIdMap[drugName] = postId;
                                                    }
                                                    // Store lowercase version for case-insensitive matching
                                                    const keyLower = key.toLowerCase();
                                                    if (!treatmentPostIdMap[keyLower]) {
                                                        treatmentPostIdMap[keyLower] = postId;
                                                    }
                                                } else {
                                                    // Treatment missing postId - will be handled during administration
                                                }
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    }
                });
            }
            
            return treatmentPostIdMap;
        } catch (error) {
            return {};
        }
    };

    /**
     * Transforms treatment data from form format to chart format
     */
    const transformTreatmentsToChartData = (
        treatments: any[], 
        treatmentPostIdMap: Record<string, number> = {}
    ): Array<{
        drug: string;
        quantity: number;
        doseAmount: string;
        doseUnit: string;
        frequency: string;
        route: string;
        stopped: string;
        treatmentPostId?: number;
        startDate?: Date;
        endDate?: Date;
        stoppedDate?: Date;
        administrations: { [dateKey: string]: { timeSlots: { [slotName: string]: TimeSlot } } };
    }> => {
        if (!Array.isArray(treatments) || treatments.length === 0) {
            return [];
        }

        return treatments.map((treatment) => {
            // Extract treatment data - handle both form format and saved format
            const drug = treatment["treatment_inpatient"] || treatment.drug || "";

            // Extract dose amount and unit - handle new fields and backward compatibility
            let doseAmount: string;
            let doseUnit: string;
            
            if (treatment["treatment-dose"] !== undefined && treatment["treatment-dose"] !== null) {
                // New format: separate amount and unit
                doseAmount = String(treatment["treatment-dose"]);
                doseUnit = String(treatment["treatment-unit"] || "");
            } else {
                // Fallback to quantity or default
                const fallbackQuantity = treatment.quantity || 1;
                doseAmount = String(fallbackQuantity);
                doseUnit = "";
            }


            const quantity = parseFloat(doseAmount) || 1;
            const frequency = treatment["treatment-frecuency"] || treatment.frequency || "";
            const route = treatment["treatment-route"] || treatment.route || "";
            
            // Determine if treatment is stopped
            const stoppedAt = treatment["treatment-stoppedAt"];
            const stopped = stoppedAt ? "1" : "0";
            
            // Extract dates - handle both string and Date formats
            let startDate: Date | undefined;
            let endDate: Date | undefined;
            let stoppedDate: Date | undefined;
            
            if (treatment["treatment-start"]) {
                startDate = getDateFromStringOrDate(treatment["treatment-start"]);
                if (startDate) {
                    startDate.setHours(0, 0, 0, 0); // Normalize to start of day
                }
            }
            
            if (treatment["treatment-finish"]) {
                endDate = getDateFromStringOrDate(treatment["treatment-finish"]);
                if (endDate) {
                    endDate.setHours(0, 0, 0, 0); // Normalize to start of day
                }
            }
            
            if (stoppedAt) {
                stoppedDate = getDateFromStringOrDate(stoppedAt);
                if (stoppedDate) {
                    stoppedDate.setHours(0, 0, 0, 0); // Normalize to start of day
                }
            }
            
            // Get treatmentPostId from multiple sources - CHECK postId FIRST (as per plan)
            let treatmentPostId = treatment.postId ||                    // First: postId from value object (per plan)
                                treatment.treatmentPostId ||             // Second: treatmentPostId from value object
                                treatment.id ||                          // Third: id (backward compatibility)
                                treatment.surveyRecordId ||               // Fourth: surveyRecordId (backward compatibility)
                                null;
            
            // If not found, try to get from treatmentPostIdMap
            if (!treatmentPostId && treatmentPostIdMap) {
                const key = `${drug}_${route}_${frequency}`;
                // Try exact match first, then drug name only, then case-insensitive match
                treatmentPostId = treatmentPostIdMap[key] || 
                                 treatmentPostIdMap[drug] || 
                                 treatmentPostIdMap[key.toLowerCase()] ||
                                 treatmentPostIdMap[drug.toLowerCase()] ||
                                 null;
            }

            return {
                drug,
                quantity,
                doseAmount,
                doseUnit,
                frequency,
                route,
                stopped,
                treatmentPostId: treatmentPostId || undefined,
                startDate,
                endDate,
                stoppedDate,
                administrations: {} // Empty initially, will be populated by chart component from API
            };
        });
    };

    const loadAdministrationsForWeek = useCallback(async (weekStartDate: Date) => {
        if (!props.patientUuid || !props.uuidInvestigation || chartDataRef.current.length === 0) {
            return;
        }

        // Create a unique key for this week
        const weekKey = formatDateToISO(weekStartDate);
        
        // If we're already loading this week, cancel the previous request
        if (loadingWeekRef.current === weekKey && abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Track that we're loading this week
        loadingWeekRef.current = weekKey;
        
        // Create new AbortController for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        try {
            // Calculate week start and end dates
            const startDate = formatDateToISO(weekStartDate); // Monday
            const weekEndDate = getWeekEnd(weekStartDate);
            const endDate = formatDateToISO(weekEndDate); // Sunday

            const response = await treatmentAdministrationService.getPatientAdministrations(
                props.uuidInvestigation || '', 
                props.patientUuid || '',
                startDate,
                endDate
            );

            // Check if request was aborted
            if (abortController.signal.aborted) {
                return;
            }

            // Response is already an array of AdministrationResponse
            const existingAdministrations = Array.isArray(response) ? response : [];
            
            // MERGE administrations instead of replacing - preserve existing administrations from other weeks
            setChartData(prevChartData => {
                // Build the new administrations map for THIS WEEK ONLY
                const administrationsMap: Record<number, Record<string, { timeSlots: { [slotName: string]: TimeSlot } }>> = {};
                
                // Process all administrations from the API response
                existingAdministrations.forEach((admin: any) => {
                    // Find the treatment in our data by treatmentPostId
                    const treatmentPostId = admin.sourcePost?.id;
                    const treatmentIndex = prevChartData.findIndex(t => t.treatmentPostId === treatmentPostId);
                    
                    if (treatmentIndex >= 0) {
                        // Parse administrationDate from fieldValues or use relatedAt
                        const adminDateStr = admin.fieldValues?.administrationDate || admin.relatedAt;
                        const adminDate = new Date(adminDateStr);
                        const dateKey = formatDateToISO(adminDate); // YYYY-MM-DD format
                        
                        // Only process dates within the current week
                        const adminDateObj = new Date(adminDateStr);
                        adminDateObj.setHours(0, 0, 0, 0);
                        const weekStartObj = new Date(weekStartDate);
                        weekStartObj.setHours(0, 0, 0, 0);
                        const weekEndObj = new Date(weekEndDate);
                        weekEndObj.setHours(0, 0, 0, 0);
                        
                        if (adminDateObj < weekStartObj || adminDateObj > weekEndObj) {
                            return; // Skip administrations outside this week
                        }
                        
                        // Get timeSlot from fieldValues (from selected row)
                        const timeSlot = (admin.fieldValues?.timeSlot || 'morning') as TimeSlotName;

                        // Initialize structure if needed
                        if (!administrationsMap[treatmentIndex]) {
                            administrationsMap[treatmentIndex] = {};
                        }
                        if (!administrationsMap[treatmentIndex][dateKey]) {
                            administrationsMap[treatmentIndex][dateKey] = { timeSlots: {} };
                        }

                        // Update with server data - backend sends string
                        const wasAdministered = admin.fieldValues?.wasAdministered === "true";
                        
                        administrationsMap[treatmentIndex][dateKey].timeSlots[timeSlot] = {
                            status: wasAdministered ? 'successful' : 'not successful',
                            administrationId: admin.id,
                            administeredBy: admin.fieldValues?.administeredBy || admin.administeredBy,
                            createdAt: admin.createdAt,
                            reasonNotAdministered: admin.fieldValues?.reasonNotAdministered
                        };
                    }
                });

                // MERGE administrations: keep existing administrations from other weeks, update only this week
                const updatedData = prevChartData.map((treatment, index) => {
                    return {
                        ...treatment,
                        administrations: administrationsMap[index] || {}
                    };
                });

                return updatedData;
            });
            
        } catch (error: any) {
            // Don't log aborted requests as errors
            if (error.name !== 'AbortError') {
                console.error('Failed to load administrations:', error);
            }
        } finally {
            // Only clear loading state if this is still the current request
            if (loadingWeekRef.current === weekKey) {
                setLoading(false);
                setInitialLoading(false);
                loadingWeekRef.current = null;
                abortControllerRef.current = null;
            }
        }
    }, [props.patientUuid, props.uuidInvestigation]);

    // Transform initialTreatments to chart data when component mounts or treatments change
    useEffect(() => {
        const updateChartData = async () => {
            const treatments = props.initialTreatments || [];
            
            if (treatments.length === 0) {
                setChartData([]);
                setInitialLoading(false);
                return;
            }

            // Fetch treatmentPostIds if we have patient and investigation info
            let treatmentPostIdMap: Record<string, number> = {};
            if (props.uuidInvestigation && props.patientUuid) {
                treatmentPostIdMap = await fetchTreatmentPostIds(props.uuidInvestigation, props.patientUuid);
            }

            // Transform treatments to chart data format
            const transformedData = transformTreatmentsToChartData(treatments, treatmentPostIdMap);
            
            setChartData(transformedData);
        };

        updateChartData();
    }, [props.initialTreatments, props.uuidInvestigation, props.patientUuid]);

    
    // Load administrations for the current week on initial mount (after chartData is ready)
    useEffect(() => {
        if (chartData.length === 0 || !props.patientUuid || !props.uuidInvestigation) {
            return;
        }

        // Get current week start (Monday-based) - same as chart default
        const today = new Date();
        const weekStart = getWeekStart(today, 'monday');
        
        loadAdministrationsForWeek(weekStart);
        
    }, [chartData.length, props.patientUuid, props.uuidInvestigation, loadAdministrationsForWeek]);

    // Handle week change from chart component - MEMOIZED to prevent infinite loops
    const handleWeekChange = useCallback((weekStartDate: Date) => {
        loadAdministrationsForWeek(weekStartDate);
    }, [loadAdministrationsForWeek]);

    /**
     * Shows user feedback message
     */
    const showMessage = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
        setShowSnackbar({
            show: true,
            message,
            severity: severity as 'success' | 'error' | 'warning' | 'info'
        });
    };

    // Generic helper to update a cell's property in the nested data structure
    const updateCellProperty = (
        drugIndex: number, 
        dateKey: string,
        timeSlotName: TimeSlotName, 
        updates: { status?: "successful" | "not successful" | null; administrationId?: number; reasonNotAdministered?: string; administeredBy?: string; createdAt?: string; }
    ) => {
        setChartData(prevData => {
            const newData = [...prevData];
            const drug = { ...newData[drugIndex] };
            const administrations = { ...drug.administrations };
            
            // Ensure the administration for this date exists
            if (!administrations[dateKey]) {
                administrations[dateKey] = { timeSlots: {} };
            }
            
            const dayAdmin = { ...administrations[dateKey] };
            const timeSlots = {...(dayAdmin.timeSlots || {})};
            
            // Ensure the timeSlot exists
            if (!timeSlots[timeSlotName]) {
                timeSlots[timeSlotName] = { status: null };
            }
            
            // Apply the updates
            timeSlots[timeSlotName] = {
                ...timeSlots[timeSlotName],
                ...updates
            };
            
            // Rebuild the structure
            dayAdmin.timeSlots = timeSlots;
            administrations[dateKey] = dayAdmin;
            drug.administrations = administrations;
            newData[drugIndex] = drug;
            
            return newData;
        });
    };

    /**
     * Enhanced status update that persists to backend
     */
    const handleStatusUpdate = async (
        drugIndex: number, 
        dateKey: string,
        timeSlotName: TimeSlotName, 
        status: "successful" | "not successful" | null,
        reasonNotAdministered?: string
    ) => {
        // Check permissions
        if (!hasWritePermission) {
            showMessage('You do not have permission to record administrations', 'error');
            return;
        }

        const treatment = chartData[drugIndex];
        
        if (!treatment?.treatmentPostId) {
            // Missing treatmentPostId - administration cannot be saved
            if (false) { // Debug flag
                console.error('❌ Missing treatmentPostId:', {
                    drugIndex,
                    treatment: treatment ? {
                        drug: treatment.drug,
                        route: treatment.route,
                        frequency: treatment.frequency,
                        treatmentPostId: treatment.treatmentPostId
                    } : 'treatment is null',
                    allTreatments: chartData.map(t => ({
                        drug: t.drug,
                        treatmentPostId: t.treatmentPostId
                    }))
                });
            }
            showMessage(`Treatment ID not found for ${treatment?.drug || 'unknown drug'}. Cannot save administration.`, 'error');
            return;
        }

        setLoading(true);

        try {
            // dateKey is already in YYYY-MM-DD format
            const administrationDate = dateKey;

            if (status === null) {
                const currentTimeSlot = treatment.administrations[dateKey]?.timeSlots[timeSlotName];
                const administrationId = currentTimeSlot?.administrationId;

                if (!administrationId) {
                    // No administration ID means it was never saved to backend, just update locally
                    updateCellProperty(drugIndex, dateKey, timeSlotName, { status: null });
                    showMessage('Administration record removed', 'warning');
                    setLoading(false);
                    return;
                }

                // Delete from backend
                try {
                    await treatmentAdministrationService.deleteAdministration(
                        props.uuidInvestigation || '',
                        treatment.treatmentPostId!,
                        administrationId
                    );
                    
                    // Update locally after successful deletion
                    updateCellProperty(drugIndex, dateKey, timeSlotName, { status: null });
                    showMessage('✅ Administration record deleted successfully', 'success');
                } catch (error: any) {
                    console.error('Failed to delete administration:', error);

                    let errorMessage = 'Failed to delete administration record';

                    if (error.message === "CANNOT_DELETE_ADMINISTRATION_MORE_THAN_2_HOURS") {
                        errorMessage = props.translate('hospital.error.CANNOT_DELETE_ADMINISTRATION_MORE_THAN_2_HOURS').toString();
                    }
                    
                    showMessage(errorMessage, 'error');
                } finally {
                    setLoading(false);
                }
                return;
            }

            // Prepare administration record - use administrationDate (YYYY-MM-DD) and timeSlot from selected row
            const administrationData = {
                treatmentPostId: treatment.treatmentPostId,
                patientUuid: props.patientUuid || '',
                wasAdministered: status === "successful",
                administrationDate: administrationDate, // YYYY-MM-DD format
                timeSlot: timeSlotName, // From selected row
                reasonNotAdministered: status === "not successful" ? (reasonNotAdministered || undefined) : undefined,
                administeredBy: getCurrentResearcherUuid() as string, // Can be added later if needed
            };

            // Save to backend
            const response = await treatmentAdministrationService.recordAdministration(
                props.uuidInvestigation || '', 
                administrationData
            );

            // Update local state
            updateCellProperty(drugIndex, dateKey, timeSlotName, { status, administrationId: response.id, reasonNotAdministered: status === "not successful" ? reasonNotAdministered: undefined, administeredBy: getCurrentResearcherUuid() as string, createdAt: response.createdAt});
            
            showMessage(
                status === "successful" 
                    ? '✅ Administration recorded successfully' 
                    : '⚠️ Non-administration recorded',
                'success'
            );
            
        } catch (error: any) {
            console.error('Failed to save administration:', error);
            
            let errorMessage = 'Failed to save administration record';
            if (error.response?.data?.errorCode) {
                const errorCode = error.response.data.errorCode;
                let apiErrorMessage = error.response.data.errorMessage;

                // Handle errorMessage as string or object
                if (typeof apiErrorMessage === 'object' && apiErrorMessage !== null) {
                    apiErrorMessage = JSON.stringify(apiErrorMessage);
                }
                errorMessage = apiErrorMessage || `Error ${errorCode}: ${treatmentAdministrationService.ERROR_MESSAGES?.[errorCode] || 'Unknown error'}`;

            } else if (error.response?.status === 403) {
                errorMessage = 'You do not have permission to record administrations';
            } else if (error.response?.status === 500) {
                errorMessage = 'Server error - please try again later';
            } else if (error.message) {

                const translationKeys = [
                    'CANNOT_RECORD_ADMINISTRATION_STOPPED_TREATMENT',
                    'CANNOT_RECORD_MORE_THAN_1_ADMINISTRATIONS_OD_FREQUENCY',
                    'CANNOT_RECORD_MORE_THAN_2_ADMINISTRATIONS_BD_FREQUENCY',
                    'CANNOT_RECORD_MORE_THAN_3_ADMINISTRATIONS_TDS_FREQUENCY',
                    'CANNOT_RECORD_DURING_DAY_ADMINISTRATIONS_NOCTE_FREQUENCY',
                    'CANNOT_RECORD_MORE_THAN_4_ADMINISTRATIONS_QID_FREQUENCY',
                    'CANNOT_RECORD_MORE_THAN_6_ADMINISTRATIONS_Q4H_FREQUENCY',
                    'CANNOT_RECORD_MORE_THAN_12_ADMINISTRATIONS_Q2H_FREQUENCY',
                    'CANNOT_RECORD_MORE_THAN_1_ADMINISTRATIONS_Q48H_FREQUENCY',
                    'CANNOT_RECORD_MORE_THAN_1_ADMINISTRATIONS_Q72H_FREQUENCY',
                    'INVALID_TIME_SLOT_FOR_FREQUENCY',
                    'DAY_NOT_APPLICABLE_FOR_FREQUENCY',
                ];

                const translationKey = translationKeys.find(
                    key => error.message.includes(key)
                );

                if (translationKey) {
                    errorMessage = props.translate(`hospital.error.${translationKey}`).toString();
                }
                else {
                    errorMessage = error.message;
                }

            }
            
            showMessage(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box position="relative">
            {initialLoading ? (
                <Box display="flex" alignItems="center" justifyContent="center" p={4}>
                    <CircularProgress size={40} />
                    <Box ml={2}>Loading existing administrations...</Box>
                </Box>
            ) : (
                <>
                    <TreatmentInpatientChartView
                        data={chartData}
                        patientUuid={props.patientUuid}
                        uuidInvestigation={props.uuidInvestigation}
                        language={props.language}
                        onStatusUpdate={handleStatusUpdate}
                        onWeekChange={handleWeekChange}
                    />
                    
                    {/* Loading overlay for API calls */}
                    {loading && (
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor="rgba(255, 255, 255, 0.7)"
                            zIndex={1000}
                        >
                            <CircularProgress size={40} />
                        </Box>
                    )}
                </>
            )}
            
            {/* Snackbar for feedback */}
            <Snackbar 
                open={showSnackbar.show} 
                autoHideDuration={4000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={showSnackbar.severity || 'success'} 
                    sx={{ width: '100%' }}
                >
                    {showSnackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default withLocalize(TreatmentInpatientChart);

